import { is, pathOr, propEq } from 'ramda'
import { useTranslation } from 'react-i18next'
import React from 'react'

import { formatConditionValue } from '../../../../../../utils/helpers/conditions'
import { useForm, useFormState } from '../../../../../../packages/kiti/components/form/form/FormHandler'
import EditableSentence from '../../EditableSentence'
import NumberInput from '../../../../../../packages/kiti/components/form/NumberInput'
import OptionSelect from '../../../../../../containers/options/select/OptionSelect'
import QuestionSelect from '../../../../../../containers/questions/select/QuestionSelect'
import Select from '../../../../../../packages/kiti/components/form/Select'
import Text from '../../../../../../packages/kiti/components/presentation/Text'

const MULTIPLE_OPERATORS = ['any', 'nany', 'exactly']

const isMultipleCondition = condition => {
  const questionType = condition?.data?.question?.typeName
  if (questionType === 'multiple') return true
  if (questionType === 'select') return false
  if (MULTIPLE_OPERATORS.includes(condition?.operator)) return true
  if (typeof condition?.origin_value === 'string' && condition.origin_value.includes('to_refs')) return true
  return false
}

const getOperatorOptions = condition => {
  if (isMultipleCondition(condition)) {
    return [
      { name: 'contém', id: 'any' },
      { name: 'não contém', id: 'nany' },
      { name: 'é exatamente', id: 'exactly' }
    ]
  }

  const questionType = condition?.data?.question?.typeName

  switch (questionType) {
    case 'numeric':
    case 'float':
      return [
        { name: 'igual a', id: 'eq' },
        { name: 'maior que', id: 'gt' },
        { name: 'maior ou igual a', id: 'gte' },
        { name: 'menor que', id: 'lt' },
        { name: 'menor ou igual a', id: 'lte' },
        { name: 'diferente de', id: 'neq' }
      ]

    case 'select':
      return [
        { name: 'igual a', id: 'eq' },
        { name: 'diferente de', id: 'neq' }
      ]

    default:
      return []
  }
}

const getValueProps = condition => {
  const question = condition?.data?.question || {}
  const questionType = question.typeName
  const isMultiple = isMultipleCondition(condition)

  if (isMultiple) {
    return {
      Input: OptionSelect,
      multiple: true,
      disableCloseOnChange: true,
      disableCloseOnBlur: true,
      idField: 'reference',
      selectOnlyId: true,
      filter: { questionIds: question.id }
    }
  }

  switch (questionType) {
    case 'numeric':
    case 'float':
      return { Input: NumberInput, disableCloseOnChange: true, selectOnlyId: true }

    case 'checkbox':
      const labels = question.data?.labels || {}
      return {
        Input: Select,
        options: [
          { name: labels.true || 'Sim', id: true },
          { name: labels.false || 'Não', id: false }
        ],
        selectOnlyId: true
      }

    case 'select':
      return {
        Input: OptionSelect,
        disableCloseOnChange: true,
        idField: 'reference',
        selectOnlyId: true,
        filter: { questionIds: question.id }
      }

    default:
      return []
  }
}

const getValueLabel = (condition, valueProps) => {
  if (is(Array, condition.value)) {
    if (!condition.value.length) return '_____'
    return formatConditionValue(condition)
  }

  let value = false
  if (!!valueProps.options?.find) {
    value = valueProps.options?.find(propEq('id', condition.value))?.name
  }
  if (!value) value = condition.value?.name
  if (!value) value = condition.value
  if (!value) value = '_____'
  return value
}

const getOperatorLabel = (condition, operatorOptions, t) => {
  const fromOptions = operatorOptions.find(propEq('id', condition.operator))?.name
  if (fromOptions) return fromOptions.toLowerCase()
  return t('conditions.operators.' + (condition.operator || 'eq')).toLowerCase()
}

const getChecklistIds = conditions => {
  const checklistIds = [
    ...conditions?.filter(item => item.field === 'form.checklist_id' || item.field === 'form_id'),
    ...conditions?.filter(item => item.field?.endsWith('checklist_id'))
  ]

  return checklistIds.map(item => {
    if (item.field === 'form_id') {
      return item.value?.checklistId
    }
    return item.value?.id
  })
}

const ReplyValueCondition = ({ children, condition, conditions, index }) => {
  const { t } = useTranslation()
  const { handleChangeIn } = useFormState()
  const form = useForm()
  const dataPath = ['conditions', index, 'data', 'question']
  const operatorOptions = getOperatorOptions(condition)
  const valueProps = getValueProps(condition)
  const checklistIds = getChecklistIds(conditions)

  return (
    <>
      <EditableSentence
        label={pathOr('__________', [...dataPath, 'name'], form)}
        path={dataPath}
        onChange={value => {
          handleChangeIn(dataPath, value)
          handleChangeIn(['conditions', index, 'value'], undefined)
          handleChangeIn(['conditions', index, 'operator'], undefined)
        }}
        Input={QuestionSelect}
        filter={{ types: ['numeric', 'float', 'checkbox', 'select', 'multiple'], checklistIds }}
      />

      <Text size="h2">for</Text>

      {!!operatorOptions?.length && (
        <EditableSentence
          label={getOperatorLabel(condition, operatorOptions, t)}
          path={['conditions', index, 'operator']}
          onChange={value => {
            handleChangeIn(['conditions', index, 'operator'], value?.id)
          }}
          Input={Select}
          options={operatorOptions}
        />
      )}

      <EditableSentence
        label={getValueLabel(condition, valueProps)}
        path={['conditions', index, 'value']}
        onChange={handleChangeIn(['conditions', index, 'value'])}
        truncate={15}
        {...valueProps}
      />
    </>
  )
}

export default ReplyValueCondition
